import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { memo } from "react";
import { useUsersContext } from "../context";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { Module, NO_DATA, PATH_CONTRACTOR } from "@/constants";
import UserUpdateForm from "./UserUpdateForm";
import UserBadge from "./UserBadge";
import { DetailsButton } from "@/components/DetailsButton";
import { DetailField } from "@/components/DetailField";

const UserDetails = memo(() => {
  const { t } = useTranslation();
  const { data: user } = useUsersContext();
  const { can_update } = useModulePermissions(Module.Users);
  const contractorsPermissions = useModulePermissions(Module.Contractors);

  if (!user) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("users.details.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <DetailField label={t("users.fields.ref_number")}>
            {user.ref_number}
          </DetailField>
          <DetailField label={t("users.fields.first_name")}>
            {user.first_name}
          </DetailField>
          <DetailField label={t("users.fields.lastName")}>
            {user.last_name}
          </DetailField>
          <DetailField label={t("users.fields.email")}>
            {user.email}
          </DetailField>
          <DetailField label={t("users.fields.phoneNumber")}>
            {user.phone_number}
          </DetailField>
          <DetailField label={t("users.fields.systemRole")}>
            {user.system_role_name}
          </DetailField>
          <DetailField label={t("users.fields.isActive")}>
            <Badge variant={user.is_active ? "secondary" : "destructive"}>
              {user.is_active
                ? t("users.status.active")
                : t("users.status.inactive")}
            </Badge>
          </DetailField>
          <DetailField label={t("users.fields.preferredLanguage")}>
            {t(`language.${user.preferred_language_iso_code}.name`, NO_DATA)}
          </DetailField>
          <DetailField label={t("users.fields.cognitoId")}>
            <span className="font-mono">{user.cognito_id}</span>
          </DetailField>
          {user.contractor_id && (
            <DetailField label={t("users.fields.contractor_is_active")}>
              <UserBadge isActive={user.contractor_is_active} />
            </DetailField>
          )}
          {user.contractor_id && contractorsPermissions.can_read && (
            <DetailField label={t("users.details.contractorCard")}>
              <DetailsButton
                path={PATH_CONTRACTOR}
                pathParams={{ contractorId: user.contractor_id }}
                label="users.details.contractorDetailsButton"
              />
            </DetailField>
          )}
        </dl>

        {can_update && (
          <>
            <Separator className="my-6" />
            <UserUpdateForm />
          </>
        )}
      </CardContent>
    </Card>
  );
});

export default UserDetails;
